import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Divider,
  Card,
  CardActionArea,
  CardContent,
  Grid,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Poll as PollIcon,
} from "@mui/icons-material";
import api from "./api";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

// Render a single answer value (rating number, skip chip, free text, or empty).
function AnswerValue({ value }) {
  if (value === null || value === undefined || value === "") {
    return <Typography variant="body2" color="text.disabled">—</Typography>;
  }
  if (typeof value === "number") {
    return <Chip label={value} size="small" color="primary" variant="outlined" />;
  }
  if (value === "not_used") {
    return <Chip label="Not used" size="small" />;
  }
  if (value === "no_opinion") {
    return <Chip label="No opinion" size="small" />;
  }
  return <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{String(value)}</Typography>;
}

// Group a flat answers array by section, preserving order of first appearance.
function groupAnswersBySection(answers) {
  const sections = [];
  const index = {};
  (answers || []).forEach((a) => {
    const key = a.sectionId || a.section || "_";
    if (!(key in index)) {
      index[key] = sections.length;
      sections.push({ id: key, title: a.section || key, items: [] });
    }
    sections[index[key]].items.push(a);
  });
  return sections;
}

function ResponseDetailDialog({ open, onClose, responseId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);

  useEffect(() => {
    if (!open || !responseId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.getSurveyResponse(responseId);
        if (!cancelled) setResponse(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || "Failed to load response");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, responseId]);

  const sections = groupAnswersBySection(response?.answers);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h6" component="div">Survey Response</Typography>
          {response && (
            <Typography variant="body2" color="text.secondary">
              {response.email} · {formatDate(response.createdAt)}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && response && (
          <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
              <Chip label={`Survey: ${response.surveyTitle || response.surveyId}`} variant="outlined" />
              {response.language && <Chip label={`Language: ${response.language}`} size="small" />}
            </Stack>

            {sections.map((section) => (
              <Box key={section.id} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {section.title}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Stack spacing={1.5}>
                  {section.items.map((item) => (
                    <Box
                      key={item.questionId}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {item.question}
                      </Typography>
                      <Box sx={{ minWidth: 120, textAlign: "right" }}>
                        <AnswerValue value={item.value} />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResponsesTable({ survey, onBack }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const fetchResponses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getSurveyResponses(survey.surveyId, {
        page,
        limit: rowsPerPage,
        search: search.trim() || undefined,
      });
      setRows(res.data || []);
      setTotal(res.pagination?.total ?? (res.data || []).length);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load responses");
    } finally {
      setLoading(false);
    }
  }, [survey.surveyId, page, rowsPerPage, search]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back to surveys
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={fetchResponses} title="Refresh"><RefreshIcon /></IconButton>
      </Stack>

      <Typography variant="h5" fontWeight="bold">
        {survey.title || survey.surveyId}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {survey.surveyId} · {total} response{total === 1 ? "" : "s"}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          label="Search by email"
          size="small"
          fullWidth
          value={search}
          onChange={(e) => {
            setPage(0);
            setSearch(e.target.value);
          }}
        />
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell>Email</TableCell>
              <TableCell>Language</TableCell>
              <TableCell>Answers</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No responses yet.</Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>
                    {row.language ? <Chip label={row.language} size="small" /> : "—"}
                  </TableCell>
                  <TableCell>
                    {Array.isArray(row.answers) ? row.answers.length : "—"}
                  </TableCell>
                  <TableCell>{formatDate(row.createdAt)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => setDetailId(row.id)} title="View response">
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      <ResponseDetailDialog
        open={Boolean(detailId)}
        responseId={detailId}
        onClose={() => setDetailId(null)}
      />
    </Box>
  );
}

function SurveysList({ onSelect }) {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSurveys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getSurveys();
      setSurveys(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load surveys");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h4" fontWeight="bold">Survey Responses</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={fetchSurveys} title="Refresh"><RefreshIcon /></IconButton>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        User submissions to the iCJ surveys. Select a survey to view its responses.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : surveys.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <PollIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography color="text.secondary">
            No survey responses have been submitted yet.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {surveys.map((survey) => (
            <Grid item xs={12} sm={6} md={4} key={survey.surveyId}>
              <Card variant="outlined">
                <CardActionArea onClick={() => onSelect(survey)}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <PollIcon color="primary" />
                      <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                        {survey.title || survey.surveyId}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {survey.surveyId}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                      <Chip
                        label={`${survey.responseCount} response${survey.responseCount === 1 ? "" : "s"}`}
                        color="primary"
                        size="small"
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
                      Last: {formatDate(survey.lastSubmittedAt)}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default function SurveyResponsesManager() {
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  return selectedSurvey ? (
    <ResponsesTable survey={selectedSurvey} onBack={() => setSelectedSurvey(null)} />
  ) : (
    <SurveysList onSelect={setSelectedSurvey} />
  );
}
